import { motion } from 'framer-motion';
import { ArrowRight, Blocks, BrainCircuit, Compass, Wrench } from 'lucide-react';

const practices = [
  {
    number: '01',
    title: 'Product & strategy',
    description:
      'Turn a broad opportunity into a focused product thesis, practical roadmap, and investment case.',
    capabilities: ['Discovery', 'Product strategy', 'Roadmapping', 'Technical diligence'],
    icon: Compass,
  },
  {
    number: '02',
    title: 'Experience & systems',
    description:
      'Design the customer journey and the operating system behind it, from first prototype to design system.',
    capabilities: ['Service design', 'UX/UI', 'Prototyping', 'Design systems'],
    icon: Blocks,
  },
  {
    number: '03',
    title: 'Software engineering',
    description:
      'Build dependable web, mobile, and cloud products with an architecture that can grow with the business.',
    capabilities: ['Full-stack apps', 'APIs', 'Cloud architecture', 'DevOps'],
    icon: Wrench,
  },
  {
    number: '04',
    title: 'AI & automation',
    description:
      'Apply AI where it creates real leverage—from voice agents to internal workflows and intelligent product features.',
    capabilities: ['Applied AI', 'Voice systems', 'Automation', 'Data products'],
    icon: BrainCircuit,
  },
];

const engagementModels = [
  'Focused advisory sprint',
  'Embedded product partner',
  'Build-to-launch team',
  'Technical rescue & modernization',
];

const Services = () => (
  <section id="services" className="bg-canvas py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-14 grid gap-8 md:grid-cols-2 md:items-end"
      >
        <div>
          <p className="section-kicker">Consulting practices</p>
          <h2 className="magazine-title mt-4 text-5xl tracking-[-0.045em] sm:text-7xl">
            Advice that ships.
          </h2>
        </div>
        <p className="max-w-xl text-lg leading-8 text-muted md:justify-self-end">
          Senior-level judgment without the handoff. Consltr can frame the problem, design the
          answer, and stay through delivery.
        </p>
      </motion.div>

      <div className="grid gap-px overflow-hidden rounded-[1.75rem] border border-line/10 bg-line/10 md:grid-cols-2">
        {practices.map((practice, index) => (
          <motion.article
            key={practice.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.08, duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
            className="service-card group bg-panel p-7 transition-colors hover:bg-panel-alt sm:p-10"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-faint">{practice.number}</span>
              <span className="service-icon flex h-11 w-11 items-center justify-center rounded-full border border-line/10">
                <practice.icon
                  size={22}
                  strokeWidth={1.5}
                  className="text-faint transition-colors group-hover:text-accent"
                  aria-hidden="true"
                />
              </span>
            </div>
            <h3 className="magazine-title mt-12 text-3xl tracking-[-0.03em] sm:text-4xl">
              {practice.title}
            </h3>
            <p className="mt-4 max-w-xl leading-7 text-muted">{practice.description}</p>
            <ul className="mt-7 grid grid-cols-2 gap-x-5 gap-y-3">
              {practice.capabilities.map(capability => (
                <li key={capability} className="flex items-center gap-2 text-sm text-faint">
                  <span className="h-1 w-1 rounded-full bg-highlight" />
                  {capability}
                </li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>

      <div className="engagement-banner mt-5 grid gap-5 rounded-[1.75rem] border border-line/10 bg-highlight p-8 text-black md:grid-cols-[1fr_auto] md:items-end sm:p-10">
        <div>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] opacity-55">
            Ways to work together
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {engagementModels.map(model => (
              <span
                key={model}
                className="rounded-full border border-black/20 px-4 py-2 text-sm font-medium"
              >
                {model}
              </span>
            ))}
          </div>
        </div>
        <a
          href="#contact"
          data-agent-id="agent://consltr/services/find-engagement"
          data-semantic-intent="navigate"
          className="inline-flex items-center gap-2 font-semibold transition-transform hover:translate-x-1"
        >
          Find the right shape
          <ArrowRight size={18} aria-hidden="true" />
        </a>
      </div>
    </div>
  </section>
);

export default Services;
